import { useMemo, useState } from "react"
import { AlertCircle, CheckCircle2, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { PlannedLotRow } from "@/features/milling/api/types"
import { useFabricateLotPlanned } from "@/features/milling/hooks/use-milling-mutations"
import { useNextLotNumber } from "@/features/milling/hooks/use-milling"
import {
  type FabricationDraft,
  validateFabrication,
} from "@/features/milling/lib/fabrication-validation"
import {
  LOT_MAX_NET_KG,
  LOT_MIN_NET_KG,
  LOT_TARGET_BAGS,
  classifyLot,
} from "@/features/milling/lib/quality"
import { formatNumber, kgToMt, mtToKg } from "@/lib/format"
import { cn } from "@/lib/utils"

// Per-Lot-Planned fabrication dialog. Opens from the fabrication list
// when the operator is ready to record the milled output for one
// planned entry.
//
// Inputs:
//   - Number of bags (max 385, defaults to the target). Cap matches
//     the backend's `lte=385` validator.
//   - Net weight in MT (user-friendly; we convert to kg on submit).
//   - Lot ID (pre-filled from /lots/next-number; operator can override).
//
// Live preview: based on `classifyLot(bags, kg)`, shows whether this
// will land as a LOT or a Queue de Lot so the operator isn't surprised
// when the chip flips on submit.
interface FabricateLotPlannedDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  usinageId: string | number
  plan: PlannedLotRow
}

export function FabricateLotPlannedDialog({
  open,
  onOpenChange,
  usinageId,
  plan,
}: FabricateLotPlannedDialogProps) {
  const { t } = useTranslation("milling")
  const fabricate = useFabricateLotPlanned(usinageId, plan.id)
  const nextLotNumber = useNextLotNumber(open)

  // Per-column cap on the actual-bags input. plannedBags is the sum
  // of the matrix cells the operator routed into this column at
  // couplage time; the operator can record any value at or below it,
  // including under-target plans that can never reach the FULL
  // threshold. Defensive fallback to LOT_TARGET_BAGS so the dialog
  // is still usable if a legacy plan row arrives with 0.
  const bagsCap = plan.plannedBags > 0 ? plan.plannedBags : LOT_TARGET_BAGS

  // Local form state. The parent re-mounts this component whenever
  // the operator opens a different planned entry (`activePlan` flips
  // in FabricationList), so we don't need an effect to reset state on
  // open — useState's initial value runs once per mount.
  const [draft, setDraft] = useState<FabricationDraft>({
    actualBags: bagsCap,
    netMt: 0,
    lotId: "",
  })
  // `lotIdDirty` flips the moment the operator types in the lot-id
  // field. Until then, the displayed value tracks the async
  // suggestion query so the field doesn't appear blank while it
  // loads, and updates if a new suggestion lands.
  const [lotIdDirty, setLotIdDirty] = useState(false)

  const netKg = useMemo(() => mtToKg(Number(draft.netMt) || 0), [draft.netMt])
  const classification = useMemo(
    () => classifyLot(Number(draft.actualBags) || 0, netKg),
    [draft.actualBags, netKg],
  )

  // Effective lot id shown in the input — operator's typed value once
  // they've edited the field, otherwise the suggestion from the API.
  // QUEUE outcomes get a server-minted QDL- number, so the field is
  // not editable in that case and the submission payload doesn't
  // carry an operator-supplied id.
  const willBeFull = classification === "FULL"
  const effectiveLotId = willBeFull
    ? lotIdDirty
      ? draft.lotId
      : (draft.lotId || nextLotNumber.data || "")
    : ""

  const submissionDraft: FabricationDraft = {
    ...draft,
    lotId: effectiveLotId,
  }

  const errors = useMemo(
    () => validateFabrication(submissionDraft, bagsCap, t as never),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submissionDraft.actualBags, submissionDraft.netMt, submissionDraft.lotId, bagsCap, t],
  )
  const canSubmit = errors.length === 0 && !fabricate.isPending

  const onSubmit = () => {
    if (!canSubmit) return
    fabricate.mutate(
      {
        actualBags: Number(submissionDraft.actualBags),
        netKg,
        lotId: submissionDraft.lotId.trim(),
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("fabricate.title", { label: plan.label })}
          </DialogTitle>
          <DialogDescription>
            {t("fabricate.subtitle", {
              plannedBags: plan.plannedBags,
              estMt: formatNumber(kgToMt(plan.estimatedWeightKg), 3),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Live classification preview */}
          <PreviewChip kind={classification} />

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fabricate-bags">
                {t("fabricate.bagsLabel")}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="fabricate-bags"
                  type="number"
                  min={0}
                  max={bagsCap}
                  value={draft.actualBags || ""}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      actualBags: Math.min(
                        bagsCap,
                        Math.max(0, Number(e.target.value) || 0),
                      ),
                    }))
                  }
                  onFocus={(e) => e.target.select()}
                  className="tabular-nums"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setDraft((d) => ({ ...d, actualBags: bagsCap }))
                  }
                  title={t("fabricate.fixAtPlannedTooltip", {
                    planned: bagsCap,
                  })}
                >
                  {bagsCap}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t("fabricate.bagsHelp", { max: bagsCap })}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fabricate-mt">
                {t("fabricate.weightLabel")}
              </Label>
              <Input
                id="fabricate-mt"
                type="number"
                min={0}
                step={0.001}
                value={draft.netMt || ""}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    netMt: Math.max(0, Number(e.target.value) || 0),
                  }))
                }
                onFocus={(e) => e.target.select()}
                placeholder={t("fabricate.weightPlaceholder")}
                className="tabular-nums"
              />
              <p className="text-[11px] text-muted-foreground">
                {t("fabricate.weightHelp", {
                  min: formatNumber(LOT_MIN_NET_KG / 1000, 1),
                  max: formatNumber(LOT_MAX_NET_KG / 1000, 1),
                })}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fabricate-lot-id">
              {t("fabricate.lotIdLabel")}
            </Label>
            <Input
              id="fabricate-lot-id"
              type="text"
              value={effectiveLotId}
              onChange={(e) => {
                setLotIdDirty(true)
                setDraft((d) => ({ ...d, lotId: e.target.value }))
              }}
              onFocus={(e) => e.target.select()}
              placeholder={
                nextLotNumber.data ?? t("fabricate.lotIdPlaceholder")
              }
              className="font-mono"
              disabled={!willBeFull}
            />
            <p className="text-[11px] text-muted-foreground">
              {willBeFull
                ? t("fabricate.lotIdHelp")
                : t("fabricate.lotIdAutoQdl")}
            </p>
          </div>

          {errors.length > 0 ? (
            <ul className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {errors.map((e, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <AlertCircle className="mt-0.5 size-3 flex-shrink-0" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={fabricate.isPending}
          >
            {t("fabricate.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {fabricate.isPending ? (
              t("fabricate.submitting")
            ) : (
              <>
                <CheckCircle2 className="size-4" />
                {t("fabricate.submit")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PreviewChip({ kind }: { kind: "FULL" | "QUEUE" }) {
  const { t } = useTranslation("milling")
  const isFull = kind === "FULL"
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md border p-2 text-xs",
        isFull
          ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
          : "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300",
      )}
    >
      <Sparkles className="size-3.5" />
      <span className="font-medium">
        {isFull
          ? t("fabricate.previewFull")
          : t("fabricate.previewQueue")}
      </span>
      <Badge variant="outline" className="ml-auto font-mono text-[10px]">
        {kind}
      </Badge>
    </div>
  )
}
